import React, { createContext, useEffect, useReducer } from 'react';

// third-party
import { Chance } from 'chance';
// import { jwtDecode } from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project imports
import Loader from 'components/Loader';
import type { AuthProps, JWTContextType, LoginWithPhoneParams } from 'types/auth';
import axios from 'utils/axios';
import { replaceAuthSessionStorage } from 'utils/authSessionStorage';

const chance = new Chance();

// constant
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

// const verifyToken: (st: string) => boolean = (serviceToken) => {
//   if (!serviceToken) {
//     return false;
//   }
//   const decoded: KeyedObject = jwtDecode(serviceToken);
//   /**
//    * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
//    */
//   return decoded.exp > Date.now() / 1000;
// };

const setSession = (
  serviceToken?: string | null,
  userId?: string | number,
  role?: string | null,
  waiterId?: number | string,
  restaurant_ids?: string[]
) => {
  if (serviceToken) {
    replaceAuthSessionStorage(localStorage, {
      serviceToken,
      userId,
      role,
      waiterId,
      restaurantIds: restaurant_ids
    });
  } else {
    localStorage.removeItem('serviceToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('role');
    localStorage.removeItem('waiterId');
    localStorage.removeItem('restaurant_ids');
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem('serviceToken');
        const userId = window.localStorage.getItem('userId');
        const role = window.localStorage.getItem('role');
        const waiterId = window.localStorage.getItem('waiterId');
        const restaurant_ids = window.localStorage.getItem('restaurant_ids');
        const restaurant_ids_array = restaurant_ids ? restaurant_ids.split(',') : undefined;
        if (serviceToken) {
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: {
                id: userId || undefined,
                role: role || undefined,
                waiterId: waiterId || undefined,
                restaurant_ids: restaurant_ids_array
              }
            }
          });
        } else {
          dispatch({
            type: LOGOUT
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: LOGOUT
        });
      }
    };

    init();
  }, []);

  const login = async (login: string, password: string) => {
    const response = await axios.post('/login', { login, password });
    const { access_token, id, restaurant_ids, role } = response.data?.data;
    setSession(access_token, id, role, undefined, restaurant_ids);
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user: {
          id,
          role,
          restaurant_ids
        }
      }
    });
    return role;
  };

  const loginWithPhone = async (params: LoginWithPhoneParams) => {
    const { accessToken, role, userId, waiterId } = params;

    // Determine role: from response first, then from path prefix, then default
    let userRole = 'customer'; // Default fallback
    if (role) {
      userRole = role;
    }
    setSession(accessToken, userId, userRole, waiterId);

    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user: {
          id: userId,
          role: userRole,
          waiterId: waiterId?.toString() || undefined
        }
      }
    });
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    // todo: this flow need to be recode as it not verified
    const id = chance.bb_pin();
    const response = await axios.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName
    });
    let users = response.data;

    if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
      const localUsers = window.localStorage.getItem('users');
      users = [
        ...JSON.parse(localUsers!),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`
        }
      ];
    }

    window.localStorage.setItem('users', JSON.stringify(users));
  };

  const logout = () => {
    setSession(null);
    dispatch({ type: LOGOUT });
  };

  const forgotPassword = async (email: string) => {
    try {
      const response = await axios.post('/password/forgot', { email });
      return response.data;
    } catch (error: any) {
      console.error('Forgot password error:', error);
      throw error;
    }
  };

  const resetPassword = async (email: string, token: string, password: string, passwordConfirmation: string) => {
    try {
      const response = await axios.post('/password/reset', {
        email,
        token,
        password,
        password_confirmation: passwordConfirmation
      });
      return response.data;
    } catch (error: any) {
      console.error('Reset password error:', error);
      throw error;
    }
  };

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext value={{ ...state, login, loginWithPhone, logout, register, forgotPassword, resetPassword, updateProfile }}>
      {children}
    </JWTContext>
  );
};

export default JWTContext;
